import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { ProlongationHistory } from '../models/prolongationHistory';
import { Recommendation, RecommendationShort } from '../models/recommendation/recommendation';

import { ConfigService } from './config.service';

/**
 * Service for recommendation-related API calls.
 * On save failure, the API returns an error body with message, reason and statusCode; use {@link getApiErrorDisplayText} to display it.
 */
@Injectable({
  providedIn: 'root'
})
export class RecommendationService {
  private http = inject(HttpClient);
  configService = inject(ConfigService);

  /**
   * Fetches recommendations by auditor area.
   * @param area - Auditor identifier.
   * @param all - If true, fetches all recommendations for the area.
   * @returns Observable of recommendation short list.
   */
  getData(area: string, all: boolean): Observable<RecommendationShort[]> {
    let endpointName = `/recommendations/${area}/`;
    if (all) {
      endpointName += 'all/';
    }
    return this.http.get<RecommendationShort[]>(`${this.configService.getConfiguration().apiHost}/api${endpointName}`);
  }

  /**
   * Fetches a single recommendation by id.
   * @param id - Recommendation id.
   * @returns Observable of the recommendation.
   */
  getDataById(id: string): Observable<Recommendation> {
    return this.http.get<Recommendation>(`${this.configService.getConfiguration().apiHost}/api/recommendation/${id}`);
  }

  /**
   * Saves or updates a recommendation. On failure the API returns an error object (message, reason, statusCode).
   * @param rec - Recommendation to save.
   * @returns Observable of the saved recommendation and optional emailWarnings (e.g. notification mail issues).
   */
  postDataById(rec: Recommendation): Observable<{ recommendation: Recommendation; emailWarnings?: string[] }> {
    return this.http.post<{ recommendation: Recommendation; emailWarnings?: string[] }>(
      `${this.configService.getConfiguration().apiHost}/api/recommendation`,
      rec
    );
  }

  /**
   * Fetches recommendations by audit id.
   * @param auditId - Audit id (this is the auditId, not the recommendation id).
   * @returns Observable of recommendation short list.
   */
  getByAudit(auditId: string): Observable<RecommendationShort[]> {
    return this.http.get<RecommendationShort[]>(
      `${this.configService.getConfiguration().apiHost}/api/recommendationsByAudit/${auditId}`
    );
  }

  /**
   * Updates recommendation state (e.g. PENDING, FINALYSETTLED). On failure the API returns an error object (message, reason, statusCode).
   * @param recommendationId - Recommendation id.
   * @param status - New status.
   * @param settledRejectReason - Optional reason when settling or rejecting.
   * @returns Observable with recommendation and optional emailWarnings (e.g. when notification mail could not be sent).
   */
  setStatus(
    recommendationId: string,
    status: string,
    settledRejectReason?: string
  ): Observable<{ recommendation: Recommendation; emailWarnings?: string[] }> {
    const body: { status: string; settledRejectReason?: string } = { status };
    if (settledRejectReason != null && settledRejectReason !== '') {
      body.settledRejectReason = settledRejectReason;
    }
    return this.http.post<{ recommendation: Recommendation; emailWarnings?: string[] }>(
      `${this.configService.getConfiguration().apiHost}/api/recommendationstate/${recommendationId}`,
      body
    );
  }

  getProlongationHistory(referenceId: string): Observable<ProlongationHistory[]> {
    return this.http.get<ProlongationHistory[]>(
      `${this.configService.getConfiguration().apiHost}/api/recommendationprolongationhistory/${referenceId}`
    );
  }
}
